import { Button, ToggleGroup, ToggleGroupItem } from "@win-reality/react-ui";
import { useState } from "react";
import { useDispatch } from "react-redux";

import {
  createSpawnedPitch,
  createSpawnedWithHit,
} from "../shared/utils/pitchGenerator";
import { TilesContainer } from "../Tagging/components/shared/TilesContainer";
import { VisualizationContainer } from "../Tagging/components/shared/VisualizationContainer";
import { ContactVis } from "../Tagging/components/shared/VisualizationContainer/visualizations/ContactVis";
import { LandingVis } from "../Tagging/components/shared/VisualizationContainer/visualizations/LandingVis";
import { StrikeZone } from "../Tagging/components/shared/VisualizationContainer/visualizations/StrikeZone";
import { addEventToQueue } from "../Tagging/store/slices/socket";
import { eventQueueTransformer } from "../Tagging/store/utils/eventQueueTransformer";
import { SingleTileContainer } from "./SingleTileContainer";

type ViewMode = "Advanced" | "Focused" | "Solo";

const VIEW_MODE_OPTIONS: { value: ViewMode; label: string }[] = [
  { value: "Advanced", label: "Advanced" },
  { value: "Focused", label: "Focused" },
  { value: "Solo", label: "Solo" },
];

export const LiveView = () => {
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState<ViewMode>("Advanced");
  const isDev = import.meta.env.DEV;

  return (
    <div className="flex flex-col gap-4 px-6">
      {/* View Mode Selector */}
      <div className="mt-6 flex">
        <ToggleGroup
          type="single"
          value={viewMode}
          onValueChange={(value) => {
            if (!value) return;
            setViewMode(value as ViewMode);
          }}
        >
          {VIEW_MODE_OPTIONS.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value}
              className="text-sm"
            >
              {option.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {/* Advanced Mode */}
      {viewMode === "Advanced" && (
        <div className="flex flex-col gap-4">
          <TilesContainer direction="row" tileType="pitching" />
          <div className="flex flex-wrap justify-center gap-4">
            <div
              className="flex flex-1 flex-col items-center justify-center rounded bg-white shadow-md"
              style={{ height: "19.3rem", minWidth: "340px" }}
            >
              <StrikeZone perspective="catcher" twoStrikeZone={false} />
            </div>
            <div
              className="flex flex-1 flex-col items-center justify-center rounded bg-white shadow-md"
              style={{ height: "19.3rem", minWidth: "340px" }}
            >
              <LandingVis style={{ width: "100%", marginBottom: "10px" }} />
            </div>
            <div
              className="flex flex-1 flex-col items-center justify-center rounded bg-white shadow-md"
              style={{ height: "19.3rem", minWidth: "340px" }}
            >
              <ContactVis style={{ width: "100%" }} />
            </div>
          </div>
          <TilesContainer direction="row" tileType="hitting" />
        </div>
      )}

      {/* Focused Mode */}
      {viewMode === "Focused" && (
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex items-center justify-center sm:w-[60%]">
            <VisualizationContainer
              withHittingOptions
              containerWidth="100%"
              containerHeight="500px"
            />
          </div>
          <div className="flex sm:w-[40%]">
            <TilesContainer
              direction="column"
              tileType="pitching"
              maxTiles={4}
            />
          </div>
        </div>
      )}

      {/* Solo Mode */}
      {viewMode === "Solo" && <SingleTileContainer />}

      {/* Dev Test Buttons */}
      {isDev && (
        <div className="flex justify-center gap-2 py-4">
          <Button
            variant="secondary-flat-light"
            onClick={() => {
              const cleanEvent = eventQueueTransformer(createSpawnedPitch());
              dispatch(addEventToQueue(cleanEvent));
            }}
          >
            Spawn Pitch
          </Button>
          <Button
            variant="secondary-flat-light"
            onClick={() => {
              const cleanEvent = eventQueueTransformer(createSpawnedWithHit());
              dispatch(addEventToQueue(cleanEvent));
            }}
          >
            Spawn Pitch + Hit
          </Button>
        </div>
      )}
    </div>
  );
};

export default LiveView;
